<template>
  <div class="users-container">
    <!-- Cabeçalho da página -->
    <div class="page-header">
      <h1 class="page-title">
        <i class="fa-solid fa-users"></i>
        Usuários
      </h1>
      <p class="page-subtitle">Gerencie os usuários do sistema</p>
    </div>

    <!-- Navegação por tabs -->
    <div class="tabs-container">
      <nav class="modern-tabs">
        <button 
          class="tab-button" 
          :class="{ active: selectedTab === 1 }" 
          @click="selectedTab = 1"
        >
          <i class="fa-solid fa-list"></i>
          LISTAGEM
        </button>
        <button 
          v-if="canCreateEditDeleteUsers"
          class="tab-button" 
          :class="{ active: selectedTab === 2 }" 
          @click="openCadastroTab"
        >
          <i class="fa-solid fa-plus tab-plus-icon"></i>
          {{ currentUser.id ? 'EDITAR' : 'CADASTRO' }}
        </button>
        <button 
          v-else
          class="tab-button disabled" 
          :disabled="true"
          :title="'Você não tem permissão para criar/editar usuários'"
        >
          <i class="fa-solid fa-plus tab-plus-icon"></i>
          CADASTRO
        </button>
      </nav>
    </div>

    <!-- Loading -->
    <div v-show="loading" class="loading-container">
      <div class="loading-spinner">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <span>Carregando...</span>
      </div>
    </div>

    <!-- Tab 1: Listagem -->
    <div v-show="selectedTab === 1 && !loading" class="tab-content">
      <div class="table-card">
        <!-- Cabeçalho da tabela -->
        <div class="table-header">
          <div class="search-container">
            <div class="search-input-group">
              <i class="fa-solid fa-search search-icon"></i>
              <input 
                type="text" 
                v-model="quickFilter" 
                class="search-input" 
                placeholder="Pesquisar usuários..." 
                autocomplete="off"
              />
            </div>
          </div>
          <span v-if="totalRows > 0" class="table-count">({{ totalRows }} usuário(s) encontrado(s))</span>
          <div v-if="totalPages > 1" class="pagination-info">
            <span>Página {{ pageIndex }} de {{ totalPages }}</span>
            <div class="pagination-controls">
              <button 
                class="btn-pagination" 
                @click="changePage(pageIndex - 1)"
                :disabled="pageIndex <= 1"
              >
                <i class="fa-solid fa-chevron-left"></i>
              </button>
              <button 
                class="btn-pagination" 
                @click="changePage(pageIndex + 1)"
                :disabled="pageIndex >= totalPages"
              >
                <i class="fa-solid fa-chevron-right"></i>
              </button>
            </div>
          </div>
          <button 
            v-if="canCreateEditDeleteUsers"
            type="button" 
            class="btn-primary" 
            @click="createUser()"
          >
            <i class="fa-solid fa-plus"></i>
            Adicionar Usuário
          </button>
        </div>

        <!-- Mensagem informativa para usuários com permissões limitadas -->
        <div v-if="!isLoggedUserAdmin" class="permission-info">
          <div class="info-card">
            <i class="fa-solid fa-info-circle"></i>
            <div class="info-content">
              <span v-if="isLoggedUserBanca">
                <strong>Modo BANCA:</strong> Você pode visualizar e gerenciar gerentes e cobradores da região ativa selecionada.
              </span>
              <span v-else-if="isLoggedUserGerente">
                <strong>Modo GERENTE:</strong> Você pode visualizar e gerenciar apenas cobradores de suas regiões.
              </span>
              <span v-else-if="isLoggedUserCobrador">
                <strong>Modo COBRADOR:</strong> Você pode visualizar apenas seus próprios dados. Criação, edição e exclusão não são permitidas.
              </span>
            </div>
          </div>
        </div>

        <!-- Filtros -->
        <div class="filters-section">
          <div class="filter-group">
            <label>Cargo:</label>
                          <select v-model="filters.role" class="filter-select">
                <option value="">Todos</option>
                <option value="0">ADMIN</option>
                <option value="3">BANCA</option>
                <option value="1">REGIÃO</option>
                <option value="4">AGENTE</option>
              </select>
          </div>
          <div class="filter-group">
            <label>Status:</label>
            <select v-model="filters.isActive" class="filter-select">
              <option value="">Todos</option>
              <option value="true">Ativo</option>
              <option value="false">Inativo</option>
            </select>
          </div>
          <div class="filter-actions">
            <button class="btn-secondary" @click="searchUsers">
              <i class="fa-solid fa-search"></i>
              Buscar
            </button>
            <button class="btn-secondary" @click="clearFilters">
              <i class="fa-solid fa-times"></i>
              Limpar
            </button>
          </div>
        </div>

        <!-- Tabela -->
        <div class="table-container">
          <table class="modern-table">
            <thead>
              <tr>
                <th style="text-align: center;">Nome</th>
                <th style="text-align: center;">Usuário</th>
                <th style="text-align: center;">Telefone</th>
                <th style="text-align: center;">Cargo</th>
                <th style="text-align: center;">Status</th>
                <th style="text-align: center;">Criado em</th>
                <th style="text-align: center;" width="140">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="user in paginatedUsers" 
                :key="user.id"
                class="table-row"
              >
                <td style="text-align: center;" class="name-column">
                  <div class="user-info">
                    <span class="user-name">{{ user.name }}</span>
                  </div>
                </td>
                <td style="text-align: center;">{{ user.username }}</td>
                <td style="text-align: center;">{{ user.phone || '-' }}</td>
                <td style="text-align: center;">
                  <span :class="['role-badge', getRoleBadgeClass(user.role)]">
                    {{ user.roleText }}
                  </span>
                </td>
                <td style="text-align: center;">
                  <span :class="['status-badge', user.isActive ? 'badge-success' : 'badge-danger']">
                    {{ user.isActive ? 'Ativo' : 'Inativo' }}
                  </span>
                </td>
                <td style="text-align: center;">{{ formatDate(user.createdAt) }}</td>
                <td style="text-align: center;">
                  <div class="action-buttons">
                    <button 
                      v-if="canEditSpecificUser(user)"
                      class="btn-action btn-edit" 
                      @click="editUser(user)"
                      title="Editar"
                    >
                      <i class="fa-solid fa-pen-to-square"></i>
                    </button>
                    <button 
                      v-if="canActivateDeactivateSpecificUser(user) && user.isActive"
                      class="btn-action btn-warning" 
                      @click="deactivateUser(user.id)"
                      title="Desativar"
                    >
                      <i class="fa-solid fa-ban"></i>
                    </button>
                    <button 
                      v-if="canActivateDeactivateSpecificUser(user) && !user.isActive"
                      class="btn-action btn-success" 
                      @click="activateUser(user.id)"
                      title="Ativar"
                    >
                      <i class="fa-solid fa-check"></i>
                    </button>
                    <button 
                      v-if="canDeleteSpecificUser(user)"
                      class="btn-action btn-delete" 
                      @click="deleteUser(user.id)"
                      title="Excluir"
                    >
                      <i class="fa-solid fa-trash"></i>
                    </button>
                    <span v-if="!canEditSpecificUser(user) && !canActivateDeactivateSpecificUser(user) && !canDeleteSpecificUser(user)" class="no-actions">
                      <i class="fa-solid fa-eye"></i>
                      Apenas visualização
                    </span>
                  </div>
                </td>
              </tr>
              <tr v-if="filteredUsers.length === 0" class="no-data">
                <td colspan="7">
                  <div class="no-data-message">
                    <i class="fa-solid fa-users"></i>
                    <span v-if="isLoggedUserBanca && !currentRegion">
                      Selecione uma região no cabeçalho para visualizar os usuários
                    </span>
                    <span v-else>
                      Nenhum usuário encontrado
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="totalPages > 1" class="pagination-footer">
          <div class="pagination-info">
            <span>Mostrando {{ startItem }}-{{ endItem }} de {{ totalRows }} resultados</span>
          </div>
          <div class="pagination-controls">
            <button 
              class="btn-pagination" 
              @click="changePage(1)"
              :disabled="pageIndex <= 1"
            >
              <i class="fa-solid fa-angles-left"></i>
            </button>
            <button 
              class="btn-pagination" 
              @click="changePage(pageIndex - 1)"
              :disabled="pageIndex <= 1"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <span class="page-info">{{ pageIndex }} / {{ totalPages }}</span>
            <button 
              class="btn-pagination" 
              @click="changePage(pageIndex + 1)"
              :disabled="pageIndex >= totalPages"
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
            <button 
              class="btn-pagination" 
              @click="changePage(totalPages)"
              :disabled="pageIndex >= totalPages"
            >
              <i class="fa-solid fa-angles-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Tab 2: Formulário -->
    <div v-show="selectedTab === 2" class="tab-content">
      <div class="form-card">
        <div class="form-header">
          <h3 class="form-title">
            <i class="fa-solid fa-users"></i>
            {{ currentUser.id ? 'Editar Usuário' : 'Novo Usuário' }}
            <p class="form-subtitle">
              {{ currentUser.id ? 'Atualize as informações do usuário' : 'Preencha as informações do novo usuário' }}
            </p>
          </h3>
        </div>

        <div class="form-body">
          <div class="form-grid">
            <div class="input-group">
              <label class="input-label required">
                <i class="fa-solid fa-user"></i>
                Nome
              </label>
              <input 
                class="form-input" 
                type="text" 
                v-model="currentUser.name"
                placeholder="Digite o nome completo..."
              />
            </div>

            <div class="input-group">
              <label class="input-label required">
                <i class="fa-solid fa-at"></i>
                Usuário
              </label>
              <input 
                class="form-input" 
                type="text" 
                v-model="currentUser.username"
                placeholder="Digite o nome de usuário..."
                autocomplete="off"
              />
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-phone"></i>
                Telefone
              </label>
              <input 
                class="form-input" 
                type="text" 
                v-model="currentUser.phone"
                placeholder="Digite o telefone..."
              />
            </div>

            <div class="input-group">
              <label class="input-label required">
                <i class="fa-solid fa-user-tag"></i>
                Cargo
              </label>
              <select v-model="currentUser.role" class="form-input">
                <option value="">Selecione o cargo</option>
                <option 
                  v-for="role in availableRolesForLoggedUser" 
                  :key="role.value" 
                  :value="role.value"
                >
                  {{ role.text }}
                </option>
              </select>
            </div>

            <!-- Campo de Banca -->
            <div class="input-group">
              <label class="input-label" :class="{ required: isLoggedUserAdmin || isLoggedUserBanca }">
                <i class="fa-solid fa-building"></i>
                Banca
              </label>
              <select v-model="currentUser.bancaId" class="form-input">
                <option value="">
                  {{ isLoggedUserAdmin || isLoggedUserBanca ? 'Selecione uma banca' : 'Selecione uma banca (opcional)' }}
                </option>
                <option 
                  v-for="banca in availableBancasForLoggedUser" 
                  :key="banca.id" 
                  :value="banca.id"
                >
                  {{ banca.name }}
                </option>
              </select>
              <small class="form-help-text">
                <i class="fa-solid fa-info-circle"></i>
                <span v-if="selectedBancaSnapshot">
                  Banca do cabeçalho: <strong>{{ selectedBancaSnapshot.name }}</strong> (pré-selecionada).
                </span>
                <span v-else>
                  Associe o usuário a uma banca. Para ADMIN, use a banca selecionada no cabeçalho.
                </span>
              </small>
            </div>

            <div class="input-group">
              <label class="input-label" :class="{ required: !currentUser.id }">
                <i class="fa-solid fa-lock"></i>
                {{ currentUser.id ? 'Nova Senha (opcional)' : 'Senha' }}
              </label>
              <input 
                class="form-input" 
                type="password" 
                v-model="currentUser.password"
                :placeholder="currentUser.id ? 'Digite a nova senha (deixe vazio para manter a atual)...' : 'Digite a senha...'"
                autocomplete="new-password"
              />
            </div>

            <!-- Região Ativa (informativo) -->
            <div class="input-group full-width">
              <div v-if="currentRegion" class="region-info-box success">
                <i class="fa-solid fa-check-circle"></i>
                <div class="info-content">
                  <strong>Região Ativa Selecionada:</strong><br>
                  O usuário será automaticamente associado à região <strong>{{ currentRegion.name }}</strong>.
                </div>
              </div>
              <div v-else class="region-info-box">
                <i class="fa-solid fa-info-circle"></i>
                <div class="info-content">
                  <strong>Informação sobre Regiões:</strong><br>
                  {{ isLoggedUserAdmin ? 'Você pode criar usuários sem região específica.' : 'Você pode criar usuários sem região ativa selecionada.' }} As regiões podem ser atribuídas posteriormente através do gerenciamento de regiões ou bancas.
                </div>
              </div>
            </div>


            <div v-if="currentUser.id" class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-toggle-on"></i>
                Status
              </label>
              <select v-model="currentUser.isActive" class="form-input">
                <option :value="true">Ativo</option>
                <option :value="false">Inativo</option>
              </select>
            </div>

            <!-- Campo Usuário Limitado (apenas admin pode ver) -->
            <div v-if="isLoggedUserAdmin" class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-user-lock"></i>
                Usuário Limitado
              </label>
              <select v-model="currentUser.isLimitedUser" class="form-input">
                <option :value="false">Não</option>
                <option :value="true">Sim</option>
              </select>
              <small class="form-help-text">
                <i class="fa-solid fa-info-circle"></i>
                Usuários limitados só podem criar outros usuários limitados.
              </small>
            </div>
          </div>
        </div>

        <div class="form-footer">
          <button 
            type="button" 
            class="btn-primary" 
            @click="currentUser.id ? updateUser() : insertUser()"
            :disabled="saving"
          >
            <i v-if="saving" class="fa-solid fa-spinner fa-spin"></i>
            <i v-else class="fa-solid fa-save"></i>
            {{ 
              saving ? 'Salvando...' : 
              (currentUser.id ? 'Atualizar' : 'Salvar') 
            }}
          </button>
          <button 
            type="button" 
            class="btn-secondary" 
            @click="reset()"
            :disabled="saving"
          >
            <i class="fa-solid fa-times"></i>
            Cancelar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de Criação de Região -->
    <div v-if="showRegionModal" class="modal-overlay" @click="closeRegionModal">
      <div class="modal-content region-modal" @click.stop>
        <div class="modal-header">
          <h3>
            <i class="fa-solid fa-map-location-dot"></i>
            Nova Região
          </h3>
          <button class="modal-close" @click="closeRegionModal">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="input-group">
            <label class="input-label required">
              <i class="fa-solid fa-tag"></i>
              Nome da Região
            </label>
            <input 
              class="form-input" 
              type="text" 
              v-model="newRegion.name"
              placeholder="Digite o nome da região..."
              @keyup.enter="createRegion"
            />
          </div>
        </div>

        <div class="modal-footer">
          <button 
            type="button" 
            class="btn-secondary" 
            @click="closeRegionModal"
          >
            <i class="fa-solid fa-times"></i>
            Cancelar
          </button>
          <button 
            type="button" 
            class="btn-primary" 
            @click="createRegion"
            :disabled="savingRegion"
          >
            <i v-if="savingRegion" class="fa-solid fa-spinner fa-spin"></i>
            <i v-else class="fa-solid fa-save"></i>
            {{ savingRegion ? 'Salvando...' : 'Criar Região' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mainService from "@/services/mainService";
import { toast } from "@/composables/useToast";
import { handleErrorMessage } from "@/services/config";
import { ROLES } from "@/services/permissions";
import jsFunctions from "@/services/jsFunctions";

export default {
  name: "ViewUsers",
  data() {
    return {
      quickFilter: "",
      users: [],
      regions: [],
      bancas: [], // Adicionar array de bancas
      selectedTab: 1,
      pageIndex: 1,
      pageSize: 10,
      loading: true,
      saving: false,
      currentUser: {
        id: null,
        name: "",
        username: "",
        phone: "",
        role: "",
        regionId: null, // Adicionar campo regionId
        bancaId: null, // Adicionar campo de banca
        password: "",
        isActive: true,
        isLimitedUser: false
      },
      filters: {
        role: "",
        isActive: ""
      },
      showRegionModal: false,
      savingRegion: false,
      newRegion: {
        name: "",
        prizeExpirationDays: 7, // Valor padrão
        requirePinForPrizeClaim: true,
        milharBonusEnabled: false,
        milharBonusMinValue: 0,
        milharBonusMaxBetValue: 10,
        milharBonusCotacao: 100,
        milharBonusMaxPrize: 1000,
        ticketFooterEnabled: false,
        ticketFooterImage: null,
        ticketFooterText: null,
        onlyDescarga: false,
        isActive: true
      },
      userRegions: [],
      selectedAdditionalRegions: [],
      selectedNewRegionInline: "",
      loggedUser: null,
      loggedUserResponsibleRegions: [],
      currentRegion: null,
      loggedUserBancaId: null,
      selectedBancaSnapshot: null
    };
  },
  computed: {
    filteredUsers() {
      let filtered = this.users;
      
      // Remover usuários com cargo COBRADOR independente do cargo do usuário logado
      filtered = filtered.filter(user => {
        const userRoleNumber = typeof user.role === 'string' ? parseInt(user.role) : user.role;
        const isCobrador = userRoleNumber === ROLES.COBRADOR || user.role === 'COBRADOR';
        return !isCobrador;
      });
      
      // Debug removido para otimização

      // Aplicar filtro baseado na região selecionada
      if (this.isLoggedUserAdmin) {
        // ADMIN: se há região selecionada, filtrar por ela. Se não há região selecionada, mostrar todos
        if (this.currentRegion) {
          filtered = filtered.filter(user => user.regionId === this.currentRegion.id);
        }
        // Se currentRegion é null, mostra todos os usuários (comportamento padrão para admin)
      } else {
        // Para usuários não-admin, aplicar filtros baseados no role e regiões do usuário logado
        if (this.isLoggedUserCobrador) {
          // COBRADOR só vê ele mesmo
          filtered = filtered.filter(user => user.id === this.loggedUser.id);
        } else if (this.isLoggedUserBanca || this.isLoggedUserGerente) {
          // BANCA e GERENTE veem usuários de suas regiões
          
          // Debug removido
          
          filtered = filtered.filter(user => {
            // Incluir o próprio usuário logado
            if (user.id === this.loggedUser.id) {
              return true;
            }
            
            // Para BANCA: incluir GERENTE e COBRADOR da região ativa
            if (this.isLoggedUserBanca) {
              // CORREÇÃO: Comparar role como número ou string
              const userRoleNumber = typeof user.role === 'string' ? parseInt(user.role) : user.role;
              const isGerente = userRoleNumber === ROLES.GERENTE || user.role === 'GERENTE';
              const isCobrador = userRoleNumber === ROLES.COBRADOR || user.role === 'COBRADOR';
              
              if (isGerente || isCobrador) {
                if (this.currentRegion && user.regionId === this.currentRegion.id) {
                  return true;
                } else {
                  return false;
                }
              } else {
                return false;
              }
            }
            
            // Para GERENTE: incluir apenas COBRADOR das regiões do usuário
            if (this.isLoggedUserGerente) {
              if (user.role === ROLES.COBRADOR) {
                return this.loggedUserResponsibleRegions.includes(user.regionId);
              }
            }
            
            return false;
          });
        }
      }

      // Filtro rápido por nome ou usuário
      if (this.quickFilter) {
        filtered = filtered.filter(user => 
          user.name.toUpperCase().includes(this.quickFilter.toUpperCase()) ||
          user.username.toUpperCase().includes(this.quickFilter.toUpperCase())
        );
      }
      return filtered;
    },

    totalRows() {
      return this.filteredUsers.length;
    },

    totalPages() {
      return Math.ceil(this.totalRows / this.pageSize) || 0;
    },

    startItem() {
      return this.totalRows === 0 ? 0 : (this.pageIndex - 1) * this.pageSize + 1;
    },

    endItem() {
      if (this.totalRows === 0) return 0;
      return Math.min(this.pageIndex * this.pageSize, this.totalRows);
    },

    paginatedUsers() {
      const start = (this.pageIndex - 1) * this.pageSize;
      return this.filteredUsers.slice(start, start + this.pageSize);
    },

    isLoggedUserAdmin() {
      return this.loggedUser && this.loggedUser.role === ROLES.ADMIN;
    },

    isLoggedUserBanca() {
      return this.loggedUser && this.loggedUser.role === ROLES.BANCA;
    },

    isLoggedUserGerente() {
      return this.loggedUser && this.loggedUser.role === ROLES.GERENTE;
    },

    isLoggedUserCobrador() {
      return this.loggedUser && this.loggedUser.role === ROLES.COBRADOR;
    },

    canCreateEditDeleteUsers() {
      // ADMIN: acesso total
      // BANCA e GERENTE: podem criar/editar/excluir
      // COBRADOR: não pode criar/editar/excluir
      return this.isLoggedUserAdmin || this.isLoggedUserBanca || this.isLoggedUserGerente;
    },



    availableRegionsForLoggedUser() {
      if (this.isLoggedUserAdmin) {
        // ADMIN vê todas as regiões
        return this.regions;
      } else {
        // Outros roles veem apenas suas regiões (principal + adicionais)
        return this.regions.filter(region => 
          this.loggedUserResponsibleRegions.includes(region.id)
        );
      }
    },



    availableBancasForLoggedUser() {
      if (!this.bancas || !Array.isArray(this.bancas)) {
        return [];
      }
      
      if (this.isLoggedUserAdmin) {
        // ADMIN vê todas as bancas
        return this.bancas;
      } else if (this.loggedUserBancaId) {
        // Usuários não-admin: mostrar apenas a banca do usuário logado
        return this.bancas.filter(banca => String(banca.id) === String(this.loggedUserBancaId));
      } else {
        // Se não tem bancaId, não mostrar nenhuma
        return [];
      }
    },

    availableRolesForLoggedUser() {
      const allRoles = [
        { value: 0, text: 'ADMIN' },
        { value: 1, text: 'REGIÃO' },
        { value: 3, text: 'BANCA' },
        { value: 4, text: 'AGENTE' }
      ];

      if (this.isLoggedUserAdmin) {
        // ADMIN pode criar todos os tipos de usuário
        return allRoles;
      } else if (this.isLoggedUserBanca) {
        // BANCA pode criar apenas GERENTE e AGENTE
        return allRoles.filter(role => role.value === 1 || role.value === 4); // GERENTE e AGENTE
      } else if (this.isLoggedUserGerente) {
        // GERENTE pode criar apenas AGENTE
        return allRoles.filter(role => role.value === 4); // AGENTE
      } else {
        // COBRADOR não pode criar usuários (já verificado em canCreateEditDeleteUsers)
        return [];
      }
    },


  },
  watch: {
    quickFilter() {
      this.pageIndex = 1;
    },
    totalPages(newTotal) {
      if (this.pageIndex > newTotal && newTotal > 0) {
        this.pageIndex = newTotal;
      } else if (newTotal === 0) {
        this.pageIndex = 1;
      }
    },
    currentRegion: {
      handler(newRegion, oldRegion) {
        this.pageIndex = 1;
        // Quando a região atual mudar, forçar reatividade da lista filtrada
        this.$forceUpdate();
      },
      deep: true
    }
  },
  async mounted() {
    this.syncSelectedBancaSnapshot();
    window.addEventListener('bancaChanged', this.onBancaChanged);

    this.loading = true;
    try {
      await this.loadLoggedUser();
      await this.loadRegions();
      await this.loadBancas();
      this.loadCurrentRegion();
      
      await this.searchUsers();
    } catch (error) {
      console.error('Erro no mounted:', error);
      this.loading = false;
      toast.error('Erro ao carregar dados iniciais');
    }
    
    // Listener para mudanças na região/banca selecionada
    this.handleStorageChange = (e) => {
      if (e.key === 'currentRegion') {
        this.loadCurrentRegion();
        this.searchUsers();
      }
      if (e.key === 'selectedBanca') {
        this.syncSelectedBancaSnapshot();
      }
    };
    
    window.addEventListener('storage', this.handleStorageChange);
  },

  beforeUnmount() {
    // Remover listener ao destruir o componente
    window.removeEventListener('storage', this.handleStorageChange);
    window.removeEventListener('bancaChanged', this.onBancaChanged);
  },

  methods: {
    readSelectedBancaFromStorage() {
      try {
        const savedBanca = localStorage.getItem('selectedBanca');
        return savedBanca ? JSON.parse(savedBanca) : null;
      } catch {
        return null;
      }
    },

    syncSelectedBancaSnapshot() {
      this.selectedBancaSnapshot = this.readSelectedBancaFromStorage();
    },

    onBancaChanged(event) {
      this.selectedBancaSnapshot = event?.detail?.banca || this.readSelectedBancaFromStorage();
    },

    resolveBancaIdForSave() {
      this.syncSelectedBancaSnapshot();

      const fromForm = this.currentUser?.bancaId || null;
      if (fromForm && String(fromForm).trim() !== '') {
        return fromForm;
      }

      if (this.isLoggedUserAdmin) {
        return this.selectedBancaSnapshot?.id || null;
      }

      if (this.isLoggedUserBanca) {
        return this.loggedUserBancaId || this.selectedBancaSnapshot?.id || null;
      }

      return this.selectedBancaSnapshot?.id || this.loggedUserBancaId || null;
    },

    // Método específico para verificar se pode editar um usuário específico
    canEditSpecificUser(user) {
      if (this.isLoggedUserAdmin) {
        return true; // ADMIN pode editar qualquer usuário
      }
      
      if (this.isLoggedUserBanca) {
        // BANCA pode editar GERENTE e COBRADOR da região ativa, e a si próprio
        if (user.id === this.loggedUser.id) return true;
        
        const userRoleNumber = typeof user.role === 'string' ? parseInt(user.role) : user.role;
        const isGerente = userRoleNumber === ROLES.GERENTE || user.role === 'GERENTE';
        const isCobrador = userRoleNumber === ROLES.COBRADOR || user.role === 'COBRADOR';
        
        return (isGerente || isCobrador) && this.currentRegion && user.regionId === this.currentRegion.id;
      }
      
      if (this.isLoggedUserGerente) {
        // GERENTE pode editar cobradores de suas regiões e a si próprio
        if (user.id === this.loggedUser.id) return true;
        const userRoleNumber = typeof user.role === 'string' ? parseInt(user.role) : user.role;
        const isCobrador = userRoleNumber === ROLES.COBRADOR || user.role === 'COBRADOR';
        return isCobrador && this.loggedUserResponsibleRegions.includes(user.regionId);
      }
      
      return false; // COBRADOR não pode editar ninguém
    },

    // Método específico para verificar se pode ativar/desativar um usuário específico
    canActivateDeactivateSpecificUser(user) {
      if (this.isLoggedUserAdmin) {
        return true; // ADMIN pode ativar/desativar qualquer usuário
      }
      
      if (this.isLoggedUserBanca) {
        // BANCA pode ativar/desativar GERENTE e COBRADOR da região ativa, mas não a si próprio
        if (user.id === this.loggedUser.id) return false; // Não pode desativar a si próprio
        
        const userRoleNumber = typeof user.role === 'string' ? parseInt(user.role) : user.role;
        const isGerente = userRoleNumber === ROLES.GERENTE || user.role === 'GERENTE';
        const isCobrador = userRoleNumber === ROLES.COBRADOR || user.role === 'COBRADOR';
        
        return (isGerente || isCobrador) && this.currentRegion && user.regionId === this.currentRegion.id;
      }
      
      if (this.isLoggedUserGerente) {
        // GERENTE pode ativar/desativar cobradores de suas regiões, mas não a si próprio
        if (user.id === this.loggedUser.id) return false;
        const userRoleNumber = typeof user.role === 'string' ? parseInt(user.role) : user.role;
        const isCobrador = userRoleNumber === ROLES.COBRADOR || user.role === 'COBRADOR';
        return isCobrador && this.loggedUserResponsibleRegions.includes(user.regionId);
      }
      
      return false; // COBRADOR não pode ativar/desativar ninguém
    },

    // Método específico para verificar se pode excluir um usuário específico
    canDeleteSpecificUser(user) {
      if (this.isLoggedUserAdmin) {
        return true; // ADMIN pode excluir qualquer usuário
      }
      
      if (this.isLoggedUserBanca) {
        // BANCA pode excluir GERENTE e COBRADOR da região ativa, mas não a si próprio
        if (user.id === this.loggedUser.id) return false; // Não pode excluir a si próprio
        
        const userRoleNumber = typeof user.role === 'string' ? parseInt(user.role) : user.role;
        const isGerente = userRoleNumber === ROLES.GERENTE || user.role === 'GERENTE';
        const isCobrador = userRoleNumber === ROLES.COBRADOR || user.role === 'COBRADOR';
        
        return (isGerente || isCobrador) && this.currentRegion && user.regionId === this.currentRegion.id;
      }
      
      if (this.isLoggedUserGerente) {
        // GERENTE pode excluir cobradores de suas regiões, mas não a si próprio
        if (user.id === this.loggedUser.id) return false;
        const userRoleNumber = typeof user.role === 'string' ? parseInt(user.role) : user.role;
        const isCobrador = userRoleNumber === ROLES.COBRADOR || user.role === 'COBRADOR';
        return isCobrador && this.loggedUserResponsibleRegions.includes(user.regionId);
      }
      
      return false; // COBRADOR não pode excluir ninguém
    },

    async loadBancas() {
      try {
        const response = await mainService.searchBancas({ isActive: true });
        this.bancas = response.data || [];
      } catch (error) {
        console.error('Erro ao carregar bancas:', error);
        this.bancas = []; // Garantir que seja um array vazio em caso de erro
      }
    },

    async loadLoggedUser() {
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          this.loggedUser = JSON.parse(userData);
          
          // Se não for ADMIN, carregar regiões do usuário responsável e bancaId
          if (this.loggedUser.role !== ROLES.ADMIN) {
            await this.loadLoggedUserResponsibleRegions();
            await this.loadLoggedUserBancaId();
          }
        }
      } catch (error) {
        console.error('Erro ao carregar dados do usuário logado:', error);
        this.loggedUser = null;
      }
    },

    async loadLoggedUserBancaId() {
      try {
        if (!this.loggedUser || !this.loggedUser.id) return;
        
        const response = await mainService.getUserBancaId(this.loggedUser.id);
        this.loggedUserBancaId = response.data || null;
      } catch (error) {
        console.error('Erro ao carregar BancaId do usuário logado:', error);
        this.loggedUserBancaId = null;
      }
    },

    async loadLoggedUserResponsibleRegions() {
      try {
        if (!this.loggedUser) return;
        
        const response = await mainService.getUserRegions(this.loggedUser.id);
        this.loggedUserResponsibleRegions = response.data.map(region => region.id);
        
        // Incluir a região principal também, se existir
        if (this.loggedUser.regionId && !this.loggedUserResponsibleRegions.includes(this.loggedUser.regionId)) {
          this.loggedUserResponsibleRegions.push(this.loggedUser.regionId);
        }
      } catch (error) {
        console.error('Erro ao carregar regiões do usuário logado:', error);
        this.loggedUserResponsibleRegions = [];
      }
    },

    loadCurrentRegion() {
      try {
        const regionData = localStorage.getItem('currentRegion');
        this.currentRegion = regionData ? JSON.parse(regionData) : null;
      } catch (error) {
        console.error('Erro ao carregar região atual:', error);
        this.currentRegion = null;
      }
    },

    openCadastroTab() {
      if (!this.canCreateEditDeleteUsers) {
        return;
      }

      // Em edição, apenas volta ao formulário; em cadastro, inicializa como o botão "Adicionar Usuário"
      if (this.currentUser.id) {
        this.selectedTab = 2;
        return;
      }

      this.createUser();
    },

    createUser() {
      if (!this.canCreateEditDeleteUsers) {
        toast.error("Você não tem permissão para criar usuários!");
        return;
      }

      this.syncSelectedBancaSnapshot();

      // Auto-gravar banca: header (ADMIN) → única disponível → banca do usuário BANCA
      let autoBancaId = null;
      if (this.isLoggedUserAdmin && this.selectedBancaSnapshot?.id) {
        autoBancaId = this.selectedBancaSnapshot.id;
      } else if (this.isLoggedUserBanca && this.loggedUserBancaId) {
        autoBancaId = this.loggedUserBancaId;
      } else {
        const availableBancas = this.availableBancasForLoggedUser;
        if (availableBancas.length === 1) {
          autoBancaId = availableBancas[0].id;
        } else if (this.selectedBancaSnapshot?.id) {
          autoBancaId = this.selectedBancaSnapshot.id;
        }
      }

      // Se o usuário logado é limitado, o novo usuário também será limitado
      let isLimitedUser = false;
      if (this.loggedUser && this.loggedUser.isLimitedUser) {
        isLimitedUser = true;
      }

      this.currentUser = {
        id: null,
        name: "",
        username: "",
        phone: "",
        role: "",
        regionId: this.currentRegion ? this.currentRegion.id : null, // Região opcional
        bancaId: autoBancaId,
        password: "",
        isActive: true,
        isLimitedUser: isLimitedUser
      };
      
      this.selectedAdditionalRegions = [];
      this.selectedNewRegionInline = "";
      this.selectedTab = 2;
    },

    async editUser(user) {
      if (!this.canEditSpecificUser(user)) {
        toast.error("Você não tem permissão para editar este usuário!");
        return;
      }

      this.currentUser = {
        id: user.id || null,
        name: user.name || "",
        username: user.username || "",
        phone: user.phone || "",
        role: this.normalizeRole(user.role),
        regionId: user.regionId || null, // Adicionar campo regionId
        bancaId: user.bancaId || null, // Adicionar campo de banca
        password: "",
        isActive: user.isActive !== undefined ? user.isActive : true,
        isLimitedUser: user.isLimitedUser || false
      };
      
      // Usar nextTick para garantir que o v-model seja atualizado
      this.$nextTick(() => {
        // Valores atualizados
      });
      
      // Regiões serão gerenciadas separadamente
      this.selectedAdditionalRegions = [];
      
      this.selectedNewRegionInline = "";
      
      // Usar setTimeout para garantir que os dados sejam setados antes de mudar a tab
      setTimeout(() => {
        this.selectedTab = 2;
      }, 50);
    },

    reset() {
      this.currentUser = {
        id: null,
        name: "",
        username: "",
        phone: "",
        role: "",
        regionId: null,
        bancaId: null, // Adicionar campo de banca
        password: "",
        isActive: true,
        isLimitedUser: false
      };
      
      this.userRegions = [];
      this.selectedAdditionalRegions = [];
      this.selectedNewRegionInline = "";
      this.selectedTab = 1;
    },

    validateUser() {
      if (!this.currentUser.name || !this.currentUser.username || this.currentUser.role === "" || this.currentUser.role === null || this.currentUser.role === undefined) {
        toast.error("Por favor, preencha todos os campos obrigatórios (Nome, Usuário e Cargo)!");
        return false;
      }
      
      // Verificar se já existe outro usuário com o mesmo nome
      const existingUserByName = this.users.find(u => 
        u.name.toUpperCase().trim() === this.currentUser.name.toUpperCase().trim() && 
        u.id !== this.currentUser.id
      );
      if (existingUserByName) {
        toast.error("Já existe um usuário cadastrado com este nome!");
        return false;
      }
      
      // Verificar se já existe outro usuário com o mesmo username
      const existingUserByUsername = this.users.find(u => 
        u.username.toUpperCase() === this.currentUser.username.toUpperCase() && 
        u.id !== this.currentUser.id
      );
      if (existingUserByUsername) {
        toast.error("Já existe um usuário cadastrado com este nome de usuário!");
        return false;
      }
      
      // Validar se o role é um número válido
      const roleNumber = parseInt(this.currentUser.role);
      if (isNaN(roleNumber) || roleNumber < 0 || roleNumber > 4) {
        toast.error("Por favor, selecione um cargo válido!");
        return false;
      }

      // Validar se o usuário logado tem permissão para criar este tipo de cargo
      const allowedRoles = this.availableRolesForLoggedUser.map(role => role.value);
      if (!allowedRoles.includes(roleNumber)) {
        const roleTexts = {
          0: 'ADMIN',
          1: 'REGIÃO', 
          2: 'COBRADOR',
          3: 'BANCA',
          4: 'AGENTE'
        };
        toast.error(`Você não tem permissão para criar usuários com cargo ${roleTexts[roleNumber]}!`);
        return false;
      }

      // REMOVIDO: Validação de região ativa obrigatória
      // Usuário pode ser criado sem região e associar depois
      
      if (!this.currentUser.id && (!this.currentUser.password || this.currentUser.password === "")) {
        toast.error("Senha é obrigatória para novos usuários!");
        return false;
      }
      
      // Validar tamanho mínimo da senha (6 caracteres)
      if (this.currentUser.password && this.currentUser.password.length < 6) {
        toast.error("A senha deve ter pelo menos 6 caracteres!");
        return false;
      }
      
      return true;
    },

    async loadRegions() {
      try {
        const response = await mainService.searchRegions();
        this.regions = response.data;
      } catch (error) {
        console.error('Erro ao buscar regiões:', error);
      }
    },

    async searchUsers() {
      this.loading = true;
      try {
        const request = {
          role: this.filters.role !== "" ? parseInt(this.filters.role) : null,
          isActive: this.filters.isActive !== "" ? this.filters.isActive === "true" : null
        };

        const response = await mainService.searchUsers(request);
        
        let users = response.data.map(user => ({
          ...user,
          roleText: this.getRoleText(user.role)
        }));

        // Forçar reatividade limpando o array antes de definir novos dados
        this.users = [];
        this.pageIndex = 1;
        this.$nextTick(() => {
          this.users = users;
        });
      } catch (error) {
        console.error('SearchUsers Error:', error);
        toast.error(handleErrorMessage(error));
      } finally {
        this.loading = false;
      }
    },

    changePage(newPage) {
      if (newPage >= 1 && newPage <= this.totalPages) {
        this.pageIndex = newPage;
      }
    },

    clearFilters() {
      this.filters = {
        role: "",
        isActive: ""
      };
      this.quickFilter = "";
      this.pageIndex = 1;
      this.searchUsers();
    },

    clearRegionFilter() {
      this.currentRegion = null;
      localStorage.removeItem('currentRegion');
      this.searchUsers();
    },

    async insertUser() {
      if (this.saving) return;

      if (!this.canCreateEditDeleteUsers) {
        toast.error("Você não tem permissão para criar usuários!");
        return;
      }

      if (this.validateUser()) {
        const bancaIdToUse = this.resolveBancaIdForSave();

        // ADMIN/BANCA: exigir banca para o vínculo funcionar
        if ((this.isLoggedUserAdmin || this.isLoggedUserBanca) && !bancaIdToUse) {
          toast.error("Selecione uma banca (no formulário ou no cabeçalho) para vincular o usuário!");
          return;
        }

        this.saving = true;
        try {
          // Se o usuário logado é limitado, forçar o novo usuário a ser limitado também
          let isLimitedUser = this.currentUser.isLimitedUser;
          if (this.loggedUser && this.loggedUser.isLimitedUser) {
            isLimitedUser = true;
          }

          // Garantir que o role seja sempre um número antes de enviar
          const userToSend = {
            ...this.currentUser,
            role: parseInt(this.currentUser.role),
            // Região é opcional - pode ser null
            regionId: this.currentUser.regionId || null,
            bancaId: bancaIdToUse,
            BancaId: bancaIdToUse,
            isLimitedUser: isLimitedUser
          };
          
          await mainService.insertUser(userToSend);
          
          toast.success("Usuário criado com sucesso!");
          this.reset();
          // Recarregar região atual do localStorage para refletir mudanças
          this.loadCurrentRegion();
          await this.searchUsers();
        } catch (error) {
          toast.error(handleErrorMessage(error));
        } finally {
          this.saving = false;
        }
      }
    },

    async updateUser() {
      if (this.saving) return;

      if (!this.canCreateEditDeleteUsers) {
        toast.error("Você não tem permissão para atualizar usuários!");
        return;
      }

      // Validação adicional para BANCA: verificar se pode editar este usuário
      if (this.isLoggedUserBanca) {
        const userToEdit = this.users.find(u => u.id === this.currentUser.id);
        if (userToEdit && !this.canEditSpecificUser(userToEdit)) {
          toast.error("Você não tem permissão para editar este usuário!");
          return;
        }
      }

      if (this.validateUser()) {
        const bancaIdToUse = this.resolveBancaIdForSave();

        if ((this.isLoggedUserAdmin || this.isLoggedUserBanca) && !bancaIdToUse) {
          toast.error("Selecione uma banca (no formulário ou no cabeçalho) para vincular o usuário!");
          return;
        }

        this.saving = true;
        try {
          // Garantir que o role seja sempre um número antes de enviar
          const userToSend = {
            ...this.currentUser,
            role: parseInt(this.currentUser.role),
            bancaId: bancaIdToUse,
            BancaId: bancaIdToUse
          };
          
          // Se a senha estiver vazia, remover do objeto para não alterar a senha atual
          if (!userToSend.password || userToSend.password.trim() === "") {
            delete userToSend.password;
          }
          
          await mainService.updateUser(userToSend);
          
          toast.success("Usuário atualizado com sucesso!");
          this.reset();
          // Recarregar região atual do localStorage para refletir mudanças
          this.loadCurrentRegion();
          await this.searchUsers();
        } catch (error) {
          // Verificar se é erro de validação de senha
          if (error.response && error.response.status === 400) {
            const errorMessage = handleErrorMessage(error);
            if (errorMessage.includes('senha') || errorMessage.includes('password') || 
                (this.currentUser.password && this.currentUser.password.length < 6)) {
              toast.error("A senha deve ter pelo menos 6 caracteres!");
            } else {
              toast.error(errorMessage);
            }
          } else {
            toast.error(handleErrorMessage(error));
          }
        } finally {
          this.saving = false;
        }
      }
    },

    async activateUser(id) {
      const user = this.users.find(u => u.id === id);
      if (!user || !this.canActivateDeactivateSpecificUser(user)) {
        toast.error("Você não tem permissão para ativar este usuário!");
        return;
      }

      try {
        await mainService.activateUser(id);
        toast.success("Usuário ativado com sucesso!");
        this.loadCurrentRegion();
        await this.searchUsers();
      } catch (error) {
        toast.error(handleErrorMessage(error));
      }
    },

    async deactivateUser(id) {
      const user = this.users.find(u => u.id === id);
      if (!user || !this.canActivateDeactivateSpecificUser(user)) {
        toast.error("Você não tem permissão para desativar este usuário!");
        return;
      }

      try {
        await mainService.deactivateUser(id);
        toast.success("Usuário desativado com sucesso!");
        this.loadCurrentRegion();
        await this.searchUsers();
      } catch (error) {
        toast.error(handleErrorMessage(error));
      }
    },

    deleteUser(id) {
      const user = this.users.find(u => u.id === id);
      if (!user || !this.canDeleteSpecificUser(user)) {
        toast.error("Você não tem permissão para excluir este usuário!");
        return;
      }

      this.$confirm({
        message: "Confirma a exclusão?",
        button: { no: "Não", yes: "Sim" },
        callback: async (confirm) => {
          if (confirm) {
            try {
              await mainService.deleteUser(id);
              toast.success("Usuário excluído com sucesso!");
              this.loadCurrentRegion();
              await this.searchUsers();
            } catch (error) {
              toast.error(handleErrorMessage(error));
            }
          }
        },
      });
      setTimeout(() => {
        document.getElementsByClassName("vc-btn left")[0].focus();
      }, 1);
    },

    formatDate(date) {
      return jsFunctions.setDateDisplay(date) || '-';
    },

    getRoleBadgeClass(role) {
      // Se é string, mapeia diretamente
      if (typeof role === 'string') {
        const roleUpper = role.toUpperCase();
        switch (roleUpper) {
          case 'ADMIN': return 'badge-admin';      // ADMIN - Vermelho
          case 'GERENTE': return 'badge-manager';  // GERENTE - Azul
          case 'COBRADOR': return 'badge-collector'; // COBRADOR - Verde
          case 'BANCA': return 'badge-manager';    // BANCA - Azul (mesma cor do GERENTE)
          case 'AGENTE': return 'badge-collector'; // AGENTE - Verde (mesma cor do COBRADOR)
          default: return 'badge-default';
        }
      }
      
      // Se é número, faz o switch numérico
      switch (role) {
        case 0: return 'badge-admin';      // ADMIN - Vermelho
        case 1: return 'badge-manager';    // GERENTE - Azul
        case 2: return 'badge-collector';  // COBRADOR - Verde
        case 3: return 'badge-manager';    // BANCA - Azul (mesma cor do GERENTE)
        case 4: return 'badge-collector';  // AGENTE - Verde (mesma cor do COBRADOR)
        default: return 'badge-default';
      }
    },

    getRoleText(role) {
      // Se já é string, retorna diretamente
      if (typeof role === 'string') {
        const roleUpper = role.toUpperCase();
        switch (roleUpper) {
          case 'ADMIN': return 'ADMIN';
          case 'GERENTE': return 'REGIÃO';
          case 'COBRADOR': return 'COBRADOR';
          case 'BANCA': return 'BANCA';
          case 'AGENTE': return 'AGENTE';
          default: return roleUpper || 'INDEFINIDO';
        }
      }
      
      // Se é número, faz o switch numérico
      switch (role) {
        case 0: return 'ADMIN';
        case 1: return 'REGIÃO';
        case 2: return 'COBRADOR';
        case 3: return 'BANCA';
        case 4: return 'AGENTE';
        default: return 'INDEFINIDO';
      }
    },

    openRegionModal() {
      this.newRegion.name = "";
      this.newRegion.prizeExpirationDays = 7;
      this.newRegion.requirePinForPrizeClaim = true;
      this.newRegion.milharBonusEnabled = false;
      this.newRegion.milharBonusMinValue = 0;
      this.newRegion.milharBonusMaxBetValue = 10;
      this.newRegion.milharBonusCotacao = 100;
      this.newRegion.milharBonusMaxPrize = 1000;
      this.newRegion.ticketFooterEnabled = false;
      this.newRegion.ticketFooterImage = null;
      this.newRegion.ticketFooterText = null;
      this.newRegion.onlyDescarga = false;
      this.newRegion.isActive = true;
      this.showRegionModal = true;
    },

    closeRegionModal() {
      this.showRegionModal = false;
      this.newRegion.name = "";
      this.newRegion.prizeExpirationDays = 7;
      this.newRegion.requirePinForPrizeClaim = true;
      this.newRegion.milharBonusEnabled = false;
      this.newRegion.milharBonusMinValue = 0;
      this.newRegion.milharBonusMaxBetValue = 10;
      this.newRegion.milharBonusCotacao = 100;
      this.newRegion.milharBonusMaxPrize = 1000;
      this.newRegion.ticketFooterEnabled = false;
      this.newRegion.ticketFooterImage = null;
      this.newRegion.ticketFooterText = null;
      this.newRegion.onlyDescarga = false;
      this.newRegion.isActive = true;
    },

    validateRegion() {
      if (!this.newRegion.name || this.newRegion.name.trim() === "") {
        toast.error("Por favor, preencha o nome da região!");
        return false;
      }
      return true;
    },

    async createRegion() {
      if (this.savingRegion) return;

      if (this.validateRegion()) {
        this.savingRegion = true;
        try {
          const response = await mainService.insertRegion(this.newRegion);
          
          // Adicionar a nova região à lista
          await this.loadRegions();
          
          // Selecionar a nova região automaticamente
          if (response.data && response.data.id) {
            this.currentUser.regionId = response.data.id;
          }
          
          toast.success("Região criada com sucesso!");
          this.closeRegionModal();
        } catch (error) {
          toast.error(handleErrorMessage(error));
        } finally {
          this.savingRegion = false;
        }
      }
    },

    async loadUserRegions() {
      if (!this.currentUser.id) return;
      
      try {
        const response = await mainService.getUserRegions(this.currentUser.id);
        this.userRegions = response.data || [];
      } catch (error) {
        console.error('Erro ao carregar regiões do usuário:', error);
        this.userRegions = [];
      }
    },





    normalizeRole(role) {
      // Tratar diferentes tipos de dados para o role
      if (role === undefined || role === null || role === '') {
        return "";
      }
      
      // Se for string, mapear para o número correspondente
      if (typeof role === 'string') {
        const roleUpper = role.toUpperCase();
        switch (roleUpper) {
          case 'ADMIN': return 0;
          case 'GERENTE': return 1;
          case 'COBRADOR': return 2;
          case 'BANCA': return 3;
          case 'AGENTE': return 4;
          default: {
            // Se for um número em string (ex: "1"), tentar converter
            const parsed = parseInt(role, 10);
            return isNaN(parsed) ? "" : parsed;
          }
        }
      }
      
      // Se for número, retornar diretamente
      if (typeof role === 'number') {
        return role;
      }
      
      // Fallback
      return "";
    }
  }
};
</script>

<style scoped>

/* Estilos para múltiplas regiões inline */
.input-group.full-width {
  grid-column: 1 / -1;
}

/* Estilo para texto de ajuda do campo de região auto-preenchida */
.form-help-text {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 0.85rem;
  color: var(--text-muted);
  line-height: 1.4;
}

.form-help-text i {
  color: var(--accent-primary);
  font-size: 0.875rem;
}

/* Estilo para caixa de informação sobre regiões */
.region-info-box {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  background-color: #e3f2fd;
  border: 1px solid #90caf9;
  border-radius: 8px;
  color: #1565c0;
}

.region-info-box.success {
  background-color: #e8f5e8;
  border-color: #4caf50;
  color: #2e7d32;
}

.region-info-box.warning {
  background-color: #fff3e0;
  border-color: #ff9800;
  color: #f57c00;
}

.region-info-box i {
  color: #1976d2;
  font-size: 1.1rem;
  margin-top: 2px;
}

.region-info-box.success i {
  color: #4caf50;
}

.region-info-box.warning i {
  color: #ff9800;
}

.info-content {
  flex: 1;
  font-size: 0.9rem;
  line-height: 1.4;
}

[data-theme="dark"] .region-info-box {
  background-color: #0d1b2a;
  border-color: #1976d2;
  color: #90caf9;
}

[data-theme="dark"] .region-info-box.success {
  background-color: #1b2e1b;
  border-color: #4caf50;
  color: #81c784;
}

[data-theme="dark"] .region-info-box.warning {
  background-color: #2e2012;
  border-color: #ff9800;
  color: #ffb74d;
}

/* Mensagem de permissão */
.permission-info {
  padding: 7px;
  background-color: #3478f2;
  border: 1px solid #548fff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: white;
  margin-bottom: 20px;
}

.info-card {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-content {
  font-size: 0.9em;
  line-height: 1.4;
}

/* Aba desabilitada */
.tab-button.disabled {
  opacity: 0.5;
  cursor: not-allowed !important;
  pointer-events: none;
}

.tab-button.disabled:hover {
  background-color: var(--bg-secondary) !important;
  color: var(--text-muted) !important;
}

/* Indicador de apenas visualização */
.no-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-muted);
  font-size: 0.875rem;
  font-style: italic;
}

.no-actions i {
  color: var(--accent-primary);
}



/* Centralização dos action-buttons */
.action-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
}

/* Centralização da user-info */
.user-info {
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Responsividade */
@media (max-width: 768px) {
  .search-container {
    flex-direction: column;
    align-items: stretch;
  }

  .pagination-footer {
    flex-direction: column;
    gap: 1rem;
  }
}

.table-count {
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 400;
  white-space: nowrap;
}

.pagination-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-pagination {
  padding: 0.5rem;
  border: 1px solid var(--card-border, var(--border-color));
  background: var(--bg-primary);
  color: var(--text-primary);
  border-radius: 6px;
  cursor: pointer;
  transition: var(--transition, all 0.2s);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.btn-pagination:hover:not(:disabled) {
  background: var(--accent-primary);
  color: white;
  border-color: var(--accent-primary);
}

.btn-pagination:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-weight: 500;
  color: var(--text-primary);
  min-width: 60px;
  text-align: center;
}

.pagination-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid var(--card-border, var(--border-color));
  background: var(--bg-primary);
}
</style>

<style>
/* Sobrescrever estilos conflitantes do vue3-toastify para evitar ícones grandes */
.Toastify__toast-container,
.Toastify__toast,
.Toastify__toast-body,
.Toastify__toast-icon {
  display: none !important;
  visibility: hidden !important;
  opacity: 0 !important;
  pointer-events: none !important;
}
</style>
